"use client";

import {
  PropertyPickerList,
  type PropertyPickerOption,
  PropertyPicker as PropertyPickerPrimitive,
} from "@repo/ui/components/property-picker";
import type * as React from "react";
import { useFieldContext } from "@/shared/forms/form-context";

interface PropertyPickerBaseProps {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
  iconOnly?: boolean;
}

export interface PropertyPickerListProps<T> extends PropertyPickerBaseProps {
  options?: PropertyPickerOption<T>[];
  renderItem?: (option: PropertyPickerOption<T>, isSelected: boolean) => React.ReactNode;
  bare?: boolean;
  /** Prepends a "None" option that clears the value */
  allowNone?: boolean;
}

function isSameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => (a as Record<string, unknown>)[k] === (b as Record<string, unknown>)[k]);
}

export function PropertyPickerListField<T>({
  options,
  renderItem,
  label,
  placeholder = "Select...",
  disabled,
  className,
  icon,
  iconOnly,
  bare,
  allowNone,
}: PropertyPickerListProps<T>) {
  const field = useFieldContext<T>();

  const allOptions = allowNone && options ? [{ value: null as unknown as T, label: "None" }, ...options] : options;
  const current = allOptions?.find((o) => isSameValue(o.value, field.state.value));
  const displayValue = current?.displayLabel ?? current?.label;
  const triggerIcon = icon ?? current?.icon;

  return (
    <PropertyPickerPrimitive<T>
      value={field.state.value}
      onChange={(val) => {
        field.handleChange(val);
        field.handleBlur();
      }}
      disabled={disabled}
      label={label}
      placeholder={placeholder}
      className={className}
      icon={triggerIcon}
      iconOnly={iconOnly}
      displayValue={displayValue}
      bare={bare}
    >
      {allOptions && <PropertyPickerList<T> options={allOptions} renderItem={renderItem} />}
    </PropertyPickerPrimitive>
  );
}

export type { PropertyPickerOption };
